import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import { API_BASE } from "../config/api";

const INITIAL_DESAFIO_FORM = {
    titulo: "",
    productoTitulo: "",
    descripcion: "",
    tieneVigencia: "1",
    activo: "1",
    imagenMiniaturaFile: null,
    imagenSecundariaFile: null,
};

const META_MONEDA_USD_ID = 2;
const ABONO_MONEDA_MXN_ID = 1; // Ajusta si MXN tiene otro ID en Champions_Moneda

const DEFAULT_META_TIPO = "IMPORTE";
const DEFAULT_ABONO_TIPO = "MONTO";
const DEFAULT_NIVEL_ACTIVO = "1";

const INITIAL_NIVEL = {
    nombre: "",
    orden: 1,
    descripcion: "",
    metaTipo: DEFAULT_META_TIPO,
    metaImporte: "",
    metaPiezas: "",
    metaMonedaId: META_MONEDA_USD_ID,
    premioNombre: "",
    premioDescripcion: "",
    abonoTipo: DEFAULT_ABONO_TIPO,
    abonoValor: "",
    abonoUrl: "",
    abonoImagenFile: null,
    abonoMonedaId: ABONO_MONEDA_MXN_ID,
    activo: DEFAULT_NIVEL_ACTIVO,
};

const INITIAL_DESAFIO_EDIT_FORM = {
    titulo: "",
    productoTitulo: "",
    descripcion: "",
    tieneVigencia: "1",
    activo: "1",

    imagenMiniaturaFile: null,
    imagenSecundariaFile: null,

    currentImagenMiniatura: null,
    currentImagenSecundaria: null,
};

function ChampionsList() {
    const [desafios, setDesafios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingDesafio, setSavingDesafio] = useState(false);
    const [savingNiveles, setSavingNiveles] = useState(false);
    const [loadingNiveles, setLoadingNiveles] = useState(false);

    const [search, setSearch] = useState("");
    const [activoFilter, setActivoFilter] = useState("");

    const [desafioForm, setDesafioForm] = useState(INITIAL_DESAFIO_FORM);

    const [selectedDesafio, setSelectedDesafio] = useState(null);
    const [currentNiveles, setCurrentNiveles] = useState([]);
    const [nivelesForm, setNivelesForm] = useState([{ ...INITIAL_NIVEL }]);

    const [editingNivel, setEditingNivel] = useState(null);
    const [savingNivelEdit, setSavingNivelEdit] = useState(false);

    const [selectedDesafioEdit, setSelectedDesafioEdit] = useState(null);
    const [editDesafioForm, setEditDesafioForm] = useState(INITIAL_DESAFIO_EDIT_FORM);
    const [savingDesafioEdit, setSavingDesafioEdit] = useState(false);

    const navigate = useNavigate();

    const showError = (message) => {
        Swal.fire({
            icon: "error",
            title: "Error",
            text: message,
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
    };

    const formatNumber = (value) => {
        if (value === null || value === undefined || value === "") return "";

        return new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(Number(value));
    };

    const showSuccess = (message) => {
        Swal.fire({
            icon: "success",
            title: "Correcto",
            text: message,
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
    };

    const openModal = (modalId) => {
        const modalEl = document.getElementById(modalId);

        if (window.bootstrap && modalEl) {
            const modal =
                window.bootstrap.Modal.getInstance(modalEl) ||
                new window.bootstrap.Modal(modalEl);

            modal.show();
        }
    };

    const closeModal = (modalId) => {
        const modalEl = document.getElementById(modalId);

        if (window.bootstrap && modalEl) {
            const modal =
                window.bootstrap.Modal.getInstance(modalEl) ||
                new window.bootstrap.Modal(modalEl);

            modal.hide();
        }
    };

    const fetchJson = async (url, options = {}) => {
        const res = await fetch(url, {
            credentials: "include",
            ...options,
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "Ocurrió un error en la petición.");
        }

        return data;
    };

    const handleDesafioFileChange = (field, file) => {
        setDesafioForm((prev) => ({
            ...prev,
            [field]: file || null,
        }));
    };

    const uploadChampionImage = async (file, tipo) => {
        if (!file) return null;

        const formData = new FormData();
        formData.append("imagen", file);
        formData.append("tipo", tipo);

        const res = await fetch(`${API_BASE}/api/admin/champions/desafios/upload-image`, {
            method: "POST",
            credentials: "include",
            body: formData,
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudo subir la imagen.");
        }

        return data.data?.url || null;
    };

    const handleEditDesafioChange = (field, value) => {
    setEditDesafioForm((prev) => ({
        ...prev,
        [field]: value,
    }));
};

const handleEditDesafioFileChange = (field, file) => {
    setEditDesafioForm((prev) => ({
        ...prev,
        [field]: file || null,
    }));
};

const openEditDesafioModal = async (desafioItem) => {
    if (!desafioItem) return;

    try {
        setSelectedDesafioEdit(desafioItem);

        setEditDesafioForm({
            titulo: desafioItem.titulo || "",
            productoTitulo: desafioItem.productoTitulo || "",
            descripcion: desafioItem.descripcion || "",
            tieneVigencia: String(desafioItem.tieneVigencia ?? "1"),
            activo: String(desafioItem.activo ?? "1"),

            imagenMiniaturaFile: null,
            imagenSecundariaFile: null,

            currentImagenMiniatura: desafioItem.imagenMiniatura || null,
            currentImagenSecundaria: desafioItem.imagenSecundaria || null,
        });

        const [recursos, videos] = await Promise.all([
            fetchRecursosByDesafio(desafioItem.desafioId),
            fetchVideosByDesafio(desafioItem.desafioId),
        ]);

        const mappedRecursos = recursos.map((recurso) => ({
            recursoId: recurso.recursoId,
            titulo: recurso.titulo || "",
            tipo: recurso.tipo || "LINK",
            url: recurso.tipo === "LINK" ? recurso.url || "" : "",
            archivoFile: null,
            currentUrl: recurso.tipo === "ARCHIVO" ? recurso.url || "" : "",
            orden: recurso.orden || 1,
            activo: String(recurso.activo ?? 1),
        }));

        const mappedVideos = videos.map((video) => ({
            championsVideoId: video.championsVideoId,
            videoId: video.videoId || "",
            videoSearch: video.titulo || video.videoTitulo || `Video ID ${video.videoId}`,
            videoTitulo: video.titulo || video.videoTitulo || "",
            orden: video.orden || 1,
            activo: String(video.activo ?? 1),
        }));

        setEditRecursosEnabled(mappedRecursos.length > 0);
        setEditSesionesEnabled(mappedVideos.length > 0);

        setEditRecursosForm(
            mappedRecursos.length > 0
                ? mappedRecursos
                : [{ ...INITIAL_RECURSO_FORM }]
        );

        setEditVideosForm(
            mappedVideos.length > 0
                ? mappedVideos
                : [{ ...INITIAL_VIDEO_FORM }]
        );

        openModal("editDesafioModal");
    } catch (error) {
        console.error("Error openEditDesafioModal:", error);
        showError(error.message || "No se pudo cargar la información adicional del desafío.");
    }
};

const handleUpdateDesafio = async (e) => {
    e.preventDefault();

    if (!validateEditDesafioForm()) return;
    if (!validateRecursos(editRecursosEnabled, editRecursosForm)) return;
    if (!validateVideos(editSesionesEnabled, editVideosForm)) return;

    if (!selectedDesafioEdit?.desafioId) {
        showError("No se pudo identificar el desafío a editar.");
        return;
    }

    try {
        setSavingDesafioEdit(true);

        const nuevaImagenMiniatura = editDesafioForm.imagenMiniaturaFile
            ? await uploadChampionImage(editDesafioForm.imagenMiniaturaFile, "miniatura")
            : editDesafioForm.currentImagenMiniatura;

        const nuevaImagenSecundaria = editDesafioForm.imagenSecundariaFile
            ? await uploadChampionImage(editDesafioForm.imagenSecundariaFile, "secundaria")
            : editDesafioForm.currentImagenSecundaria;

        const payload = {
            titulo: editDesafioForm.titulo.trim(),
            productoTitulo: editDesafioForm.productoTitulo.trim() || null,
            descripcion: cleanHtml(editDesafioForm.descripcion),
            imagenMiniatura: nuevaImagenMiniatura,
            imagenSecundaria: nuevaImagenSecundaria,
            tieneVigencia: Number(editDesafioForm.tieneVigencia),
            activo: Number(editDesafioForm.activo),
        };

        const res = await fetch(
            `${API_BASE}/api/admin/champions/desafios/${selectedDesafioEdit.desafioId}`,
            {
                method: "PUT",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            }
        );

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudo actualizar el desafío.");
        }

        await saveRecursosForDesafio({
            desafioId: selectedDesafioEdit.desafioId,
            enabled: editRecursosEnabled,
            recursos: editRecursosForm,
        });

        await saveVideosForDesafio({
            desafioId: selectedDesafioEdit.desafioId,
            enabled: editSesionesEnabled,
            videos: editVideosForm,
        });

        closeModal("editDesafioModal");

        setSelectedDesafioEdit(null);
        setEditDesafioForm(INITIAL_DESAFIO_EDIT_FORM);

        await fetchDesafios();

        showSuccess("Desafío actualizado correctamente.");
    } catch (error) {
        console.error("Error handleUpdateDesafio:", error);
        showError(error.message || "No se pudo actualizar el desafío.");
    } finally {
        setSavingDesafioEdit(false);
    }
};

const validateEditDesafioForm = () => {
    if (!editDesafioForm.titulo.trim()) {
        showError("El título del desafío es requerido.");
        return false;
    }

    if (editDesafioForm.titulo.trim().length > 180) {
        showError("El título no puede exceder 180 caracteres.");
        return false;
    }

    if (editDesafioForm.productoTitulo.trim().length > 180) {
        showError("El título del producto no puede exceder 180 caracteres.");
        return false;
    }

    return true;
};

    // Niveles

    const mapNivelToEditForm = (nivel) => {
    return {
        nivelId: nivel.nivelId,

        nombre: nivel.nombre || "",
        orden: nivel.orden || 1,
        descripcion: nivel.descripcion || "",

        metaTipo: "IMPORTE",
        metaImporte: nivel.metaImporte || "",
        metaPiezas: "",
        metaMonedaId: META_MONEDA_USD_ID,

        premioNombre: nivel.premioNombre || "",
        premioDescripcion: nivel.premioDescripcion || "",

        abonoTipo: "MONTO",
        abonoValor: nivel.abonoValor || "",
        bonoUrl: nivel.abonoUrl || "",
        abonoImagenFile: null,
        abonoMonedaId: ABONO_MONEDA_MXN_ID,

        activo: "1",
    };
    };

    const handleEditNivel = (nivel) => {
        setEditingNivel(mapNivelToEditForm(nivel));
    };

    const handleCancelEditNivel = () => {
        setEditingNivel(null);
    };

    const updateEditingNivelField = (field, value) => {
        setEditingNivel((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const reloadNivelesBySelectedDesafio = async () => {
        if (!selectedDesafio?.desafioId) return;

        const data = await fetchJson(
            `${API_BASE}/api/admin/champions/desafios/${selectedDesafio.desafioId}`
        );

        const niveles = data.data?.niveles || [];

        setCurrentNiveles(niveles);
    };

    const validateEditingNivel = () => {
        if (!editingNivel?.nivelId) {
            showError("No se pudo identificar el nivel a editar.");
            return false;
        }

        if (!editingNivel.nombre.trim()) {
            showError("El nombre del nivel es requerido.");
            return false;
        }

        if (editingNivel.nombre.trim().length > 120) {
            showError("El nombre del nivel no puede exceder 120 caracteres.");
            return false;
        }

        const orden = Number(editingNivel.orden);

        if (!Number.isInteger(orden) || orden <= 0) {
            showError("El orden debe ser un entero positivo.");
            return false;
        }

        const duplicatedOrder = currentNiveles.some(
            (nivel) =>
                Number(nivel.orden) === orden &&
                Number(nivel.nivelId) !== Number(editingNivel.nivelId)
        );

        if (duplicatedOrder) {
            showError(`Ya existe otro nivel con el orden ${orden}.`);
            return false;
        }

        if (!editingNivel.metaImporte || Number(editingNivel.metaImporte) <= 0) {
            showError("La meta importe USD debe ser mayor a 0.");
            return false;
        }

        if (
            editingNivel.premioNombre &&
            editingNivel.premioNombre.trim().length > 180
        ) {
            showError("El nombre del premio no puede exceder 180 caracteres.");
            return false;
        }

        if (editingNivel.abonoValor !== "" && Number(editingNivel.abonoValor) < 0) {
            showError("El valor del abono MXN debe ser mayor o igual a 0.");
            return false;
        }

        return true;
    };

    const handleUpdateNivel = async () => {
        if (!validateEditingNivel()) return;

        try {
            setSavingNivelEdit(true);

            const payload = buildNivelPayload(editingNivel);

            await fetchJson(
                `${API_BASE}/api/admin/champions/niveles/${editingNivel.nivelId}`,
                {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            await reloadNivelesBySelectedDesafio();
            await fetchDesafios();

            setEditingNivel(null);

            showSuccess("Nivel actualizado correctamente.");
        } catch (error) {
            console.error("Error handleUpdateNivel:", error);
            showError(error.message || "No se pudo actualizar el nivel.");
        } finally {
            setSavingNivelEdit(false);
        }
    };

    const fetchDesafios = async () => {
        try {
            setLoading(true);

            const params = new URLSearchParams();

            if (search.trim()) {
                params.append("search", search.trim());
            }

            if (activoFilter !== "") {
                params.append("activo", activoFilter);
            }

            const data = await fetchJson(
                `${API_BASE}/api/admin/champions/desafios?${params.toString()}`
            );

            const items = Array.isArray(data.data)
                ? data.data
                : data.data?.items || [];

            setDesafios(items);
        } catch (error) {
            console.error("Error fetchDesafios:", error);
            showError(error.message || "No se pudieron consultar los desafíos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDesafios();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchDesafios();
        }, 400);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, activoFilter]);

    const handleDesafioChange = (field, value) => {
        setDesafioForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const resetDesafioForm = () => {
        setDesafioForm(INITIAL_DESAFIO_FORM);
        setCreateRecursosEnabled(false);
        setCreateSesionesEnabled(false);
        setCreateRecursosForm([{ ...INITIAL_RECURSO_FORM }]);
        setCreateVideosForm([{ ...INITIAL_VIDEO_FORM }]);
    };

    const cleanHtml = (html) => {
        if (!html) return null;

        const value = String(html).trim();

        if (value === "<p><br></p>" || value === "") {
            return null;
        }

        return value;
    };

    const validateDesafioForm = () => {
        if (!desafioForm.titulo.trim()) {
            showError("El título del desafío es requerido.");
            return false;
        }

        if (desafioForm.titulo.trim().length > 180) {
            showError("El título del desafío no puede exceder 180 caracteres.");
            return false;
        }

        if (desafioForm.productoTitulo.trim().length > 180) {
            showError("El título del producto no puede exceder 180 caracteres.");
            return false;
        }

        return true;
    };

    const handleCreateDesafio = async (e) => {
    e.preventDefault();

    if (!validateDesafioForm()) return;
    if (!validateRecursos(createRecursosEnabled, createRecursosForm)) return;
    if (!validateVideos(createSesionesEnabled, createVideosForm)) return;

    try {
        setSavingDesafio(true);

        const imagenMiniatura = await uploadChampionImage(
            desafioForm.imagenMiniaturaFile,
            "miniatura"
        );

        const imagenSecundaria = await uploadChampionImage(
            desafioForm.imagenSecundariaFile,
            "secundaria"
        );

        const payload = {
            titulo: desafioForm.titulo.trim(),
            productoTitulo: desafioForm.productoTitulo.trim() || null,
            descripcion: cleanHtml(desafioForm.descripcion),
            imagenMiniatura,
            imagenSecundaria,
            tieneVigencia: Number(desafioForm.tieneVigencia),
            activo: Number(desafioForm.activo),
        };

        const res = await fetch(`${API_BASE}/api/admin/champions/desafios`, {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudo crear el desafío.");
        }

        const createdDesafioId =
            data.data?.desafioId ||
            data.data?.Desafio_ID ||
            data.data?.id;

        if (!createdDesafioId) {
            throw new Error("El desafío se creó, pero no se recibió el ID para guardar recursos/sesiones.");
        }

        await saveRecursosForDesafio({
            desafioId: createdDesafioId,
            enabled: createRecursosEnabled,
            recursos: createRecursosForm,
        });

        await saveVideosForDesafio({
            desafioId: createdDesafioId,
            enabled: createSesionesEnabled,
            videos: createVideosForm,
        });

        closeModal("addDesafioModal");
        resetDesafioForm();
        await fetchDesafios();

        showSuccess("Desafío creado correctamente.");
    } catch (error) {
        console.error("Error handleCreateDesafio:", error);
        showError(error.message || "No se pudo crear el desafío.");
    } finally {
        setSavingDesafio(false);
    }
};

    const clearFilters = () => {
        setSearch("");
        setActivoFilter("");
    };

    const getNextOrder = (niveles = []) => {
        if (!niveles.length) return 1;

        const maxOrder = Math.max(
            ...niveles.map((nivel) => Number(nivel.orden || nivel.Nivel_Orden || 0))
        );

        return maxOrder + 1;
    };

    const mapNivelToForm = (nivel) => {
        return {
            nivelId: nivel.nivelId,
            isNew: false,

            nombre: nivel.nombre || "",
            orden: nivel.orden || 1,
            descripcion: nivel.descripcion || "",

            metaTipo: "IMPORTE",
            metaImporte: nivel.metaImporte || "",
            metaPiezas: "",
            metaMonedaId: META_MONEDA_USD_ID,

            premioNombre: nivel.premioNombre || "",
            premioDescripcion: nivel.premioDescripcion || "",

            abonoTipo: "MONTO",
            abonoValor: nivel.abonoValor || "",
            abonoMonedaId: ABONO_MONEDA_MXN_ID,

            activo: "1",
        };
    };

    const buildNewNivelForm = (orden = 1) => {
        return {
            ...INITIAL_NIVEL,
            isNew: true,
            nivelId: null,
            orden,
            metaTipo: "IMPORTE",
            metaMonedaId: META_MONEDA_USD_ID,
            abonoTipo: "MONTO",
            abonoMonedaId: ABONO_MONEDA_MXN_ID,
            activo: "1",
        };
    };

    const openNivelesModal = async (desafio) => {
    try {
        setSelectedDesafio(desafio);
        setCurrentNiveles([]);
        setEditingNivel(null);
        setLoadingNiveles(true);

        setNivelesForm([
            buildNewNivelForm(Number(desafio.totalNiveles || 0) + 1),
        ]);

        openModal("nivelesDesafioModal");

        const data = await fetchJson(
            `${API_BASE}/api/admin/champions/desafios/${desafio.desafioId}`
        );

        const niveles = data.data?.niveles || [];

        setCurrentNiveles(niveles);

        if (niveles.length > 0) {
            setNivelesForm(niveles.map(mapNivelToForm));
        } else {
            setNivelesForm([buildNewNivelForm(1)]);
        }
    } catch (error) {
        console.error("Error openNivelesModal:", error);
        showError(error.message || "No se pudieron consultar los niveles.");
    } finally {
        setLoadingNiveles(false);
    }
};

    const updateNivelField = (index, field, value) => {
        setNivelesForm((prev) =>
            prev.map((nivel, i) =>
                i === index
                    ? {
                          ...nivel,
                          [field]: value,
                      }
                    : nivel
            )
        );
    };

    const addNivelRow = () => {
    setNivelesForm((prev) => {
        const usedOrders = prev.map((nivel) => Number(nivel.orden || 0));
        const nextOrder = usedOrders.length ? Math.max(...usedOrders) + 1 : 1;

        return [
            ...prev,
            buildNewNivelForm(nextOrder),
        ];
    });
};

    const removeNivelRow = (index) => {
    setNivelesForm((prev) => {
        const nivel = prev[index];

        if (nivel?.nivelId) {
            showError("Este nivel ya existe. Por ahora solo puedes editarlo, no eliminarlo.");
            return prev;
        }

        return prev.filter((_, i) => i !== index);
    });
};

    const validateNivelesForm = () => {
    if (!selectedDesafio?.desafioId) {
        showError("No se pudo identificar el desafío seleccionado.");
        return false;
    }

    if (!nivelesForm.length) {
        showError("Debes agregar al menos un nivel.");
        return false;
    }

    const orders = new Set();

    for (let i = 0; i < nivelesForm.length; i++) {
        const nivel = nivelesForm[i];
        const label = `Nivel ${i + 1}`;

        if (!nivel.nombre.trim()) {
            showError(`${label}: el nombre es requerido.`);
            return false;
        }

        if (nivel.nombre.trim().length > 120) {
            showError(`${label}: el nombre no puede exceder 120 caracteres.`);
            return false;
        }

        const orden = Number(nivel.orden);

        if (!Number.isInteger(orden) || orden <= 0) {
            showError(`${label}: el orden debe ser un entero positivo.`);
            return false;
        }

        if (orders.has(orden)) {
            showError(`${label}: el orden ${orden} está repetido.`);
            return false;
        }

        orders.add(orden);

        if (!nivel.metaImporte || Number(nivel.metaImporte) <= 0) {
            showError(`${label}: la meta importe USD debe ser mayor a 0.`);
            return false;
        }

        if (nivel.premioNombre && nivel.premioNombre.trim().length > 180) {
            showError(`${label}: el nombre del premio no puede exceder 180 caracteres.`);
            return false;
        }

        if (nivel.abonoValor !== "" && Number(nivel.abonoValor) < 0) {
            showError(`${label}: el valor del abono MXN debe ser mayor o igual a 0.`);
            return false;
        }
    }

    return true;
};

    const buildNivelPayload = async (nivel) => {
        const abonoUrlFinal = nivel.abonoImagenFile
            ? await uploadChampionImage(nivel.abonoImagenFile, "premio")
            : nivel.abonoUrl || null;

        return {
            nombre: nivel.nombre.trim(),
            orden: Number(nivel.orden),
            descripcion: nivel.descripcion.trim() || null,

            metaTipo: DEFAULT_META_TIPO,
            metaImporte: Number(nivel.metaImporte),
            metaPiezas: null,
            metaMonedaId: META_MONEDA_USD_ID,

            premioNombre: nivel.premioNombre.trim() || null,
            premioDescripcion: nivel.premioDescripcion.trim() || null,

            abonoTipo: DEFAULT_ABONO_TIPO,
            abonoValor:
                nivel.abonoValor !== "" && nivel.abonoValor !== null && nivel.abonoValor !== undefined
                    ? Number(nivel.abonoValor)
                    : null,
            abonoUrl: abonoUrlFinal,
            abonoMonedaId: ABONO_MONEDA_MXN_ID,

            activo: Number(DEFAULT_NIVEL_ACTIVO),
        };
    };

    const handleSaveNiveles = async (e) => {
    e.preventDefault();

    if (!validateNivelesForm()) return;

    try {
        setSavingNiveles(true);

        for (const nivel of nivelesForm) {
            const payload = await buildNivelPayload(nivel);

            if (nivel.nivelId) {
                await fetchJson(
                    `${API_BASE}/api/admin/champions/niveles/${nivel.nivelId}`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                    }
                );
            } else {
                await fetchJson(
                    `${API_BASE}/api/admin/champions/desafios/${selectedDesafio.desafioId}/niveles`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(payload),
                    }
                );
            }
        }

        closeModal("nivelesDesafioModal");

        setSelectedDesafio(null);
        setCurrentNiveles([]);
        setNivelesForm([buildNewNivelForm(1)]);

        await fetchDesafios();

        showSuccess("Nivel(es) guardados correctamente.");
    } catch (error) {
        console.error("Error handleSaveNiveles:", error);
        showError(error.message || "No se pudieron guardar los niveles.");
    } finally {
        setSavingNiveles(false);
    }
};

// Recursos
const INITIAL_RECURSO_FORM = {
    recursoId: null,
    titulo: "",
    tipo: "LINK", // LINK | ARCHIVO
    url: "",
    archivoFile: null,
    currentUrl: "",
    orden: 1,
    activo: "1",
};

const INITIAL_VIDEO_FORM = {
    championsVideoId: null,
    videoId: "",
    videoSearch: "",
    videoTitulo: "",
    orden: 1,
    activo: "1",
};

const [createRecursosEnabled, setCreateRecursosEnabled] = useState(false);
const [createSesionesEnabled, setCreateSesionesEnabled] = useState(false);
const [createRecursosForm, setCreateRecursosForm] = useState([{ ...INITIAL_RECURSO_FORM }]);
const [createVideosForm, setCreateVideosForm] = useState([{ ...INITIAL_VIDEO_FORM }]);

const [editRecursosEnabled, setEditRecursosEnabled] = useState(false);
const [editSesionesEnabled, setEditSesionesEnabled] = useState(false);
const [editRecursosForm, setEditRecursosForm] = useState([{ ...INITIAL_RECURSO_FORM }]);
const [editVideosForm, setEditVideosForm] = useState([{ ...INITIAL_VIDEO_FORM }]);

const [videoSearchResults, setVideoSearchResults] = useState({});
const [videoSearchLoading, setVideoSearchLoading] = useState({});

const getNextOrden = (items = []) => {
    if (!items.length) return 1;

    return Math.max(...items.map((item) => Number(item.orden || 0))) + 1;
};

const updateArrayField = (setter, index, field, value) => {
    setter((prev) =>
        prev.map((item, i) =>
            i === index
                ? {
                    ...item,
                    [field]: value,
                }
                : item
        )
    );
};

const addRecursoRow = (setter, currentRows) => {
    setter((prev) => [
        ...prev,
        {
            ...INITIAL_RECURSO_FORM,
            orden: getNextOrden(currentRows),
        },
    ]);
};

const removeRecursoRow = (setter, index) => {
    setter((prev) => {
        if (prev.length <= 1) return prev;

        return prev.filter((_, i) => i !== index);
    });
};

const addVideoRow = (setter, currentRows) => {
    setter((prev) => [
        ...prev,
        {
            ...INITIAL_VIDEO_FORM,
            orden: getNextOrden(currentRows),
        },
    ]);
};

const removeVideoRow = (setter, index) => {
    setter((prev) => {
        if (prev.length <= 1) return prev;

        return prev.filter((_, i) => i !== index);
    });
};

const fetchRecursosByDesafio = async (desafioId) => {
    const res = await fetch(`${API_BASE}/api/admin/champions/desafios/${desafioId}/recursos`, {
        method: "GET",
        credentials: "include",
    });

    const data = await res.json();

    if (!res.ok || !data.success) {
        throw new Error(data.message || "No se pudieron consultar los recursos.");
    }

    return data.data || [];
};

const fetchVideosByDesafio = async (desafioId) => {
    const res = await fetch(`${API_BASE}/api/admin/champions/desafios/${desafioId}/videos`, {
        method: "GET",
        credentials: "include",
    });

    const data = await res.json();

    if (!res.ok || !data.success) {
        throw new Error(data.message || "No se pudieron consultar las sesiones.");
    }

    return data.data || [];
};

const validateRecursos = (enabled, recursos) => {
    if (!enabled) return true;

    for (let i = 0; i < recursos.length; i++) {
        const recurso = recursos[i];
        const label = `Recurso ${i + 1}`;

        if (!recurso.titulo.trim()) {
            showError(`${label}: el título es requerido.`);
            return false;
        }

        if (recurso.tipo === "LINK" && !recurso.url.trim()) {
            showError(`${label}: la URL es requerida.`);
            return false;
        }

        if (
            recurso.tipo === "LINK" &&
            recurso.url.trim() &&
            !/^https?:\/\//i.test(recurso.url.trim())
        ) {
            showError(`${label}: la URL debe iniciar con http:// o https://.`);
            return false;
        }

        if (
            recurso.tipo === "ARCHIVO" &&
            !recurso.archivoFile &&
            !recurso.currentUrl &&
            !recurso.recursoId
        ) {
            showError(`${label}: debes seleccionar un archivo PDF.`);
            return false;
        }

        if (!Number.isInteger(Number(recurso.orden)) || Number(recurso.orden) <= 0) {
            showError(`${label}: el orden debe ser entero positivo.`);
            return false;
        }
    }

    return true;
};

const searchVideoCatalog = async ({ query, rowKey }) => {
    try {
        if (!query || query.trim().length < 2) {
            setVideoSearchResults((prev) => ({
                ...prev,
                [rowKey]: [],
            }));
            return;
        }

        setVideoSearchLoading((prev) => ({
            ...prev,
            [rowKey]: true,
        }));

        const params = new URLSearchParams();
        params.append("search", query.trim());
        params.append("limit", "20");

        const res = await fetch(
            `${API_BASE}/api/admin/champions/videos/catalogo?${params.toString()}`,
            {
                method: "GET",
                credentials: "include",
            }
        );

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudieron buscar videos.");
        }

        setVideoSearchResults((prev) => ({
            ...prev,
            [rowKey]: data.data || [],
        }));
    } catch (error) {
        console.error("Error searchVideoCatalog:", error);

        setVideoSearchResults((prev) => ({
            ...prev,
            [rowKey]: [],
        }));
    } finally {
        setVideoSearchLoading((prev) => ({
            ...prev,
            [rowKey]: false,
        }));
    }
};

const validateVideos = (enabled, videos) => {
    if (!enabled) return true;

    for (let i = 0; i < videos.length; i++) {
        const video = videos[i];
        const label = `Sesión ${i + 1}`;

        if (!video.videoId || !Number.isInteger(Number(video.videoId)) || Number(video.videoId) <= 0) {
            showError(`${label}: debes buscar y seleccionar una sesión válida.`);
            return false;
        }

        if (!Number.isInteger(Number(video.orden)) || Number(video.orden) <= 0) {
            showError(`${label}: el orden debe ser entero positivo.`);
            return false;
        }
    }

    return true;
};

const saveRecursosForDesafio = async ({ desafioId, enabled, recursos }) => {
    if (!enabled) return;

    for (const recurso of recursos) {
        const formData = new FormData();

        formData.append("titulo", recurso.titulo.trim());
        formData.append("tipo", recurso.tipo);
        formData.append("orden", String(recurso.orden || 1));
        formData.append("activo", String(recurso.activo ?? "1"));

        if (recurso.tipo === "LINK") {
            formData.append("url", recurso.url.trim());
        }

        if (recurso.tipo === "ARCHIVO") {
            if (recurso.archivoFile) {
                formData.append("archivo", recurso.archivoFile);
            }

            if (recurso.currentUrl) {
                formData.append("url", recurso.currentUrl);
            }
        }

        const isEdit = Boolean(recurso.recursoId);

        const url = isEdit
            ? `${API_BASE}/api/admin/champions/desafios/${desafioId}/recursos/${recurso.recursoId}`
            : `${API_BASE}/api/admin/champions/desafios/${desafioId}/recursos`;

        const res = await fetch(url, {
            method: isEdit ? "PUT" : "POST",
            credentials: "include",
            body: formData,
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudo guardar un recurso.");
        }
    }
};

const saveVideosForDesafio = async ({ desafioId, enabled, videos }) => {
    if (!enabled) return;

    for (const video of videos) {
        const payload = {
            videoId: Number(video.videoId),
            orden: Number(video.orden || 1),
            activo: Number(video.activo ?? 1),
        };

        const isEdit = Boolean(video.championsVideoId);

        const url = isEdit
            ? `${API_BASE}/api/admin/champions/desafios/${desafioId}/videos/${video.championsVideoId}`
            : `${API_BASE}/api/admin/champions/desafios/${desafioId}/videos`;

        const res = await fetch(url, {
            method: isEdit ? "PUT" : "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudo guardar una sesión.");
        }
    }
};

const renderRecursosFields = ({
    enabled,
    setEnabled,
    recursos,
    setRecursos,
    disabled,
}) => (
    <div className="col-md-12">
        <div className="mb-3 form-check form-switch">
            <input
                className="form-check-input"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={disabled}
            />
            <label className="form-check-label">
                ¿Este desafío requiere recursos?
            </label>
        </div>

            {enabled && (
                <div className="border rounded p-3 mb-4 bg-light-subtle">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 className="mb-0">Recursos útiles</h6>
                            <small className="text-muted">
                                Agrega links o archivos PDF relacionados con el desafío.
                            </small>
                        </div>
                        <div className="d-flex justify-content-end mb-3">
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => addRecursoRow(setRecursos, recursos)}
                                disabled={disabled}
                            >
                                Agregar recurso
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        {recursos.map((recurso, index) => (
                            <div className="card border mb-3" key={recurso.recursoId || `recurso-${index}`}>
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <strong>
                                        {recurso.recursoId ? `Recurso existente #${recurso.recursoId}` : `Nuevo recurso ${index + 1}`}
                                    </strong>

                                    {recursos.length > 1 && !recurso.recursoId && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => removeRecursoRow(setRecursos, index)}
                                            disabled={disabled}
                                        >
                                            Eliminar
                                        </button>
                                    )}
                                </div>

                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label">Título del recurso *</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={recurso.titulo}
                                                onChange={(e) =>
                                                    updateArrayField(setRecursos, index, "titulo", e.target.value)
                                                }
                                                disabled={disabled}
                                                maxLength={180}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label">Tipo</label>
                                            <select
                                                className="form-select"
                                                value={recurso.tipo}
                                                onChange={(e) =>
                                                    updateArrayField(setRecursos, index, "tipo", e.target.value)
                                                }
                                                disabled={disabled}
                                            >
                                                <option value="LINK">Link</option>
                                                <option value="ARCHIVO">Archivo</option>
                                            </select>
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label">Orden</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={recurso.orden}
                                                onChange={(e) =>
                                                    updateArrayField(setRecursos, index, "orden", e.target.value)
                                                }
                                                disabled={disabled}
                                                min="1"
                                            />
                                        </div>

                                        {recurso.tipo === "LINK" ? (
                                            <div className="col-md-12">
                                                <label className="form-label">URL del recurso *</label>
                                                <input
                                                    type="url"
                                                    className="form-control"
                                                    value={recurso.url}
                                                    onChange={(e) =>
                                                        updateArrayField(setRecursos, index, "url", e.target.value)
                                                    }
                                                    disabled={disabled}
                                                    placeholder="https://..."
                                                />
                                            </div>
                                        ) : (
                                            <div className="col-md-12">
                                                <label className="form-label">Archivo del recurso</label>

                                                {recurso.currentUrl ? (
                                                    <div className="mb-2">
                                                        <a
                                                            href={recurso.currentUrl}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="btn btn-link p-0"
                                                        >
                                                            Ver archivo actual
                                                        </a>
                                                    </div>
                                                ) : null}

                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept="application/pdf,.pdf"
                                                    onChange={(e) =>
                                                        updateArrayField(
                                                            setRecursos,
                                                            index,
                                                            "archivoFile",
                                                            e.target.files?.[0] || null
                                                        )
                                                    }
                                                    disabled={disabled}
                                                />

                                                {recurso.archivoFile ? (
                                                    <small className="text-success">
                                                        Archivo seleccionado: {recurso.archivoFile.name}
                                                    </small>
                                                ) : null}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        
    </div>
);

const renderSesionesFields = ({
    enabled,
    setEnabled,
    videos,
    setVideos,
    disabled,
}) => (
    <div className="col-md-12">
        <div className="mb-3 form-check form-switch">
            <input
                className="form-check-input"
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                disabled={disabled}
            />
            <label className="form-check-label">
                ¿Este desafío requiere sesiones?
            </label>
        </div>

            {enabled && (
                <div className="border rounded p-3 mb-4 bg-light-subtle">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                            <h6 className="mb-0">Sesiones recomendadas</h6>
                            <small className="text-muted">
                                Agrega Video_ID del catálogo. El routeKey se resolverá dinámicamente en el site.
                            </small>
                        </div>
                        <div className="d-flex justify-content-end mb-3">
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => addVideoRow(setVideos, videos)}
                                disabled={disabled}
                            >
                                Agregar sesión
                            </button>
                        </div>
                    </div>
                    <div className="card-body">
                        {videos.map((video, index) => (
                            <div className="card border mb-3" key={video.championsVideoId || `video-${index}`}>
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <strong>
                                        {video.championsVideoId ? `Sesión existente #${video.championsVideoId}` : `Nueva sesión ${index + 1}`}
                                    </strong>

                                    {videos.length > 1 && !video.championsVideoId && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={() => removeVideoRow(setVideos, index)}
                                            disabled={disabled}
                                        >
                                            Eliminar
                                        </button>
                                    )}
                                </div>

                                <div className="card-body">
                                    <div className="row g-3">
                                        <div className="col-md-6 position-relative">
                                            <label className="form-label">Buscar sesión *</label>

                                            <input
                                                type="text"
                                                className="form-control"
                                                value={video.videoSearch || ""}
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const rowKey = video.championsVideoId || `new-${index}`;

                                                    updateArrayField(setVideos, index, "videoSearch", value);
                                                    updateArrayField(setVideos, index, "videoTitulo", "");
                                                    updateArrayField(setVideos, index, "videoId", "");

                                                    searchVideoCatalog({
                                                        query: value,
                                                        rowKey,
                                                    });
                                                }}
                                                disabled={disabled}
                                                placeholder="Escribe el nombre del video..."
                                                autoComplete="off"
                                            />

                                            {video.videoId ? (
                                                // <small className="text-success">
                                                //     Video seleccionado ID: {video.videoId}
                                                // </small>
                                                <></>
                                            ) : (
                                                <small className="text-muted">
                                                    Escribe al menos 2 caracteres y selecciona una sesión.
                                                </small>
                                            )}

                                            {videoSearchLoading[video.championsVideoId || `new-${index}`] ? (
                                                <div className="small text-muted mt-1">Buscando...</div>
                                            ) : null}

                                            {Array.isArray(videoSearchResults[video.championsVideoId || `new-${index}`]) &&
                                                videoSearchResults[video.championsVideoId || `new-${index}`].length > 0 && (
                                                    <div
                                                        className="list-group position-absolute w-100 shadow"
                                                        style={{
                                                            zIndex: 9999,
                                                            maxHeight: "260px",
                                                            overflowY: "auto",
                                                        }}
                                                    >
                                                        {videoSearchResults[video.championsVideoId || `new-${index}`].map((item) => (
                                                            <button
                                                                type="button"
                                                                key={item.videoId}
                                                                className="list-group-item list-group-item-action"
                                                                onClick={() => {
                                                                    const rowKey = video.championsVideoId || `new-${index}`;

                                                                    updateArrayField(setVideos, index, "videoId", item.videoId);
                                                                    updateArrayField(setVideos, index, "videoSearch", item.titulo);
                                                                    updateArrayField(setVideos, index, "videoTitulo", item.titulo);

                                                                    setVideoSearchResults((prev) => ({
                                                                        ...prev,
                                                                        [rowKey]: [],
                                                                    }));
                                                                }}
                                                            >
                                                                <div className="fw-semibold">
                                                                    {item.titulo}
                                                                </div>

                                                                <div className="small text-muted">
                                                                    {/* ID: {item.videoId} */}
                                                                    {item.instructor ? ` · ${item.instructor}` : ""}
                                                                    {item.duracion ? ` · ${item.duracion}` : ""} Min
                                                                </div>
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label">Orden</label>
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={video.orden}
                                                onChange={(e) =>
                                                    updateArrayField(setVideos, index, "orden", e.target.value)
                                                }
                                                disabled={disabled}
                                                min="1"
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label">Estado</label>
                                            <select
                                                className="form-select"
                                                value={video.activo}
                                                onChange={(e) =>
                                                    updateArrayField(setVideos, index, "activo", e.target.value)
                                                }
                                                disabled={disabled}
                                            >
                                                <option value="1">Activo</option>
                                                <option value="0">Inactivo</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        
    </div>
);



    return (
        <>
            <Sidebar />

            <main className="main-content">
                <Header />

                <div className="content-inner container-fluid pb-0" id="page_layout">
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                        <div className="d-flex flex-column">
                            <h3>Desafios Champions</h3>
                            <p className="text-primary mb-0">Panduit Core</p>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-end">
                                    <div className="row g-2 align-items-end w-100">
                                        <div className="col-md-4">
                                            <label className="form-label mb-1">Buscar</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Título"
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                        </div>

                                        <div className="col-md-3">
                                            <label className="form-label mb-1">Estado</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={activoFilter}
                                                onChange={(e) => setActivoFilter(e.target.value)}
                                            >
                                                <option value="">Todos</option>
                                                <option value="1">Activos</option>
                                                <option value="0">Inactivos</option>
                                            </select>
                                        </div>

                                        <div className="col-md-5 d-flex justify-content-end gap-2 ms-auto">
                                            <button
                                                type="button"
                                                className="btn btn-secondary-subtle btn-sm"
                                                title="Limpiar filtros"
                                                onClick={clearFilters}
                                            >
                                                Limpiar
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                                data-bs-toggle="modal"
                                                data-bs-target="#addDesafioModal"
                                            >
                                                Agregar Desafio
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="card-body px-0">
                                    <div className="table-responsive">
                                        <table
                                            id="user-list-table"
                                            className="table table-striped"
                                            role="grid"
                                            data-toggle="data-table"
                                        >
                                            <thead>
                                                <tr className="ligth">
                                                    <th>ID</th>
                                                    <th>Titulo</th>
                                                    <th>Producto</th>
                                                    <th>Niveles</th>
                                                    <th>Estado</th>
                                                    <th>Acción</th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {loading ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">
                                                            <div className="d-flex flex-column align-items-center justify-content-center gap-2 py-4">
                                                                <div className="col-md-12 col-sm-4 mb-4">
                                                                    <div className="iq-loader-box">
                                                                        <div className="iq-loader-1"></div>
                                                                    </div>
                                                                </div>

                                                                <span className="text-muted">
                                                                    Cargando Desafios...
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ) : desafios.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="text-center py-4">
                                                            <span className="text-muted">
                                                                No hay desafíos registrados.
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    desafios.map((desafio) => (
                                                        <tr key={desafio.desafioId}>
                                                            <td>{desafio.desafioId}</td>

                                                            <td>
                                                                <strong>{desafio.titulo}</strong>
                                                            </td>

                                                            <td>{desafio.productoTitulo || "-"}</td>

                                                            <td>{desafio.totalNiveles || 0}</td>

                                                            <td>
                                                                {Number(desafio.activo) === 1 ? (
                                                                    <span className="badge bg-success-subtle p-2 text-success">
                                                                        Activo
                                                                    </span>
                                                                ) : (
                                                                    <span className="badge bg-danger-subtle p-2 text-danger">
                                                                        Inactivo
                                                                    </span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <div className="d-flex">
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-info-subtle ms-2"
                                                                    onClick={() => navigate(`/champions/desafios/${desafio.desafioId}`)}
                                                                >
                                                                    <span className="btn-inner">
                                                                        <svg
                                                                            className="icon-32"
                                                                            width="32"
                                                                            viewBox="0 0 24 24"
                                                                            fill="none"
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                        >
                                                                            <path
                                                                                opacity="0.4"
                                                                                fillRule="evenodd"
                                                                                clipRule="evenodd"
                                                                                d="M17.7366 6.04606C19.4439 7.36388 20.8976 9.29455 21.9415 11.7091C22.0195 11.8924 22.0195 12.1067 21.9415 12.2812C19.8537 17.1103 16.1366 20 12 20H11.9902C7.86341 20 4.14634 17.1103 2.05854 12.2812C1.98049 12.1067 1.98049 11.8924 2.05854 11.7091C4.14634 6.87903 7.86341 4 11.9902 4H12C14.0683 4 16.0293 4.71758 17.7366 6.04606ZM8.09756 12C8.09756 14.1333 9.8439 15.8691 12 15.8691C14.1463 15.8691 15.8927 14.1333 15.8927 12C15.8927 9.85697 14.1463 8.12121 12 8.12121C9.8439 8.12121 8.09756 9.85697 8.09756 12Z"
                                                                                fill="currentColor"
                                                                            />
                                                                            <path
                                                                                d="M14.4308 11.997C14.4308 13.3255 13.3381 14.4115 12.0015 14.4115C10.6552 14.4115 9.5625 13.3255 9.5625 11.997C9.5625 11.8321 9.58201 11.678 9.61128 11.5228H9.66006C10.743 11.5228 11.621 10.6695 11.6601 9.60184C11.7674 9.58342 11.8845 9.57275 12.0015 9.57275C13.3381 9.57275 14.4308 10.6588 14.4308 11.997Z"
                                                                                fill="currentColor"
                                                                            />
                                                                        </svg>
                                                                    </span>
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-warning-subtle ms-2"
                                                                    onClick={() => openEditDesafioModal(desafio)}
                                                                >
                                                                    <span class="btn-inner"><svg class="icon-32" width="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="0.4" d="M19.9927 18.9534H14.2984C13.7429 18.9534 13.291 19.4124 13.291 19.9767C13.291 20.5422 13.7429 21.0001 14.2984 21.0001H19.9927C20.5483 21.0001 21.0001 20.5422 21.0001 19.9767C21.0001 19.4124 20.5483 18.9534 19.9927 18.9534Z" fill="currentColor"></path><path d="M10.309 6.90385L15.7049 11.2639C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8344 7.72908 20.8452L4.23696 20.8882C4.05071 20.8903 3.88775 20.7613 3.84542 20.5764L3.05175 17.1258C2.91419 16.4915 3.05175 15.8358 3.45388 15.3306L9.88256 6.95545C9.98627 6.82108 10.1778 6.79743 10.309 6.90385Z" fill="currentColor"></path><path opacity="0.4" d="M18.1208 8.66544L17.0806 9.96401C16.9758 10.0962 16.7874 10.1177 16.6573 10.0124C15.3927 8.98901 12.1545 6.36285 11.2561 5.63509C11.1249 5.52759 11.1069 5.33625 11.2127 5.20295L12.2159 3.95706C13.126 2.78534 14.7133 2.67784 15.9938 3.69906L17.4647 4.87078C18.0679 5.34377 18.47 5.96726 18.6076 6.62299C18.7663 7.3443 18.597 8.0527 18.1208 8.66544Z" fill="currentColor"></path></svg></span>
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-icon btn-secondary-subtle ms-2"
                                                                    onClick={() => openNivelesModal(desafio)}
                                                                >
                                                                <span className="btn-inner">
                                                                    <svg class="icon-32" width="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">                                <path opacity="0.4" d="M2 12C2 6.485 6.486 2 12 2C17.514 2 22 6.485 22 12C22 17.514 17.514 22 12 22C6.486 22 2 17.514 2 12Z" fill="currentColor"></path>                                <path d="M7.77942 13.4425C7.77942 13.2515 7.85242 13.0595 7.99842 12.9135L11.4684 9.42652C11.6094 9.28552 11.8004 9.20652 12.0004 9.20652C12.1994 9.20652 12.3904 9.28552 12.5314 9.42652L16.0034 12.9135C16.2954 13.2065 16.2954 13.6805 16.0014 13.9735C15.7074 14.2655 15.2324 14.2645 14.9404 13.9715L12.0004 11.0185L9.06042 13.9715C8.76842 14.2645 8.29442 14.2655 8.00042 13.9735C7.85242 13.8275 7.77942 13.6345 7.77942 13.4425Z" fill="currentColor"></path>                                </svg>    
                                                                </span>                        
                                                                </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </main>

            {/* MODAL AGREGAR DESAFÍO */}
            <div
                className="modal fade"
                id="addDesafioModal"
                tabIndex="-1"
                aria-labelledby="addDesafioModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <form onSubmit={handleCreateDesafio}>
                            <div className="modal-header">
                                <h5 className="modal-title" id="addDesafioModalLabel">
                                    Agregar Desafío
                                </h5>

                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    disabled={savingDesafio}
                                ></button>
                            </div>

                            <div className="modal-body"
                            style={{
                                    maxHeight: "calc(100vh - 180px)",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                }}>
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Título del desafío *
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={desafioForm.titulo}
                                            onChange={(e) =>
                                                handleDesafioChange("titulo", e.target.value)
                                            }
                                            maxLength={180}
                                            disabled={savingDesafio}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Título del producto
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={desafioForm.productoTitulo}
                                            onChange={(e) =>
                                                handleDesafioChange(
                                                    "productoTitulo",
                                                    e.target.value
                                                )
                                            }
                                            maxLength={180}
                                            disabled={savingDesafio}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Imagen principal
                                        </label>

                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/png,image/jpeg,image/webp"
                                            onChange={(e) =>
                                                handleDesafioFileChange(
                                                    "imagenMiniaturaFile",
                                                    e.target.files?.[0]
                                                )
                                            }
                                            disabled={savingDesafio}
                                        />

                                        {desafioForm.imagenMiniaturaFile && (
                                            <small className="text-muted">
                                                Archivo seleccionado: {desafioForm.imagenMiniaturaFile.name}
                                            </small>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Imagen secundaria
                                        </label>

                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/png,image/jpeg,image/webp"
                                            onChange={(e) =>
                                                handleDesafioFileChange(
                                                    "imagenSecundariaFile",
                                                    e.target.files?.[0]
                                                )
                                            }
                                            disabled={savingDesafio}
                                        />

                                        {desafioForm.imagenSecundariaFile && (
                                            <small className="text-muted">
                                                Archivo seleccionado: {desafioForm.imagenSecundariaFile.name}
                                            </small>
                                        )}
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">
                                            ¿Tiene vigencia?
                                        </label>
                                        <select
                                            className="form-select"
                                            value={desafioForm.tieneVigencia}
                                            onChange={(e) =>
                                                handleDesafioChange(
                                                    "tieneVigencia",
                                                    e.target.value
                                                )
                                            }
                                            disabled={savingDesafio}
                                        >
                                            <option value="1">Sí</option>
                                            <option value="0">No</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Estado</label>
                                        <select
                                            className="form-select"
                                            value={desafioForm.activo}
                                            onChange={(e) =>
                                                handleDesafioChange("activo", e.target.value)
                                            }
                                            disabled={savingDesafio}
                                        >
                                            <option value="1">Activo</option>
                                            <option value="0">Inactivo</option>
                                        </select>
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label">Descripción</label>
                                        <ReactQuill
                                            theme="snow"
                                            value={desafioForm.descripcion}
                                            onChange={(value) =>
                                                handleDesafioChange("descripcion", value)
                                            }
                                            readOnly={savingDesafio}
                                        />
                                    </div>

                                    {renderRecursosFields({
                                        enabled: createRecursosEnabled,
                                        setEnabled: setCreateRecursosEnabled,
                                        recursos: createRecursosForm,
                                        setRecursos: setCreateRecursosForm,
                                        disabled: savingDesafio,
                                    })}

                                    {renderSesionesFields({
                                        enabled: createSesionesEnabled,
                                        setEnabled: setCreateSesionesEnabled,
                                        videos: createVideosForm,
                                        setVideos: setCreateVideosForm,
                                        disabled: savingDesafio,
                                    })}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={savingDesafio}
                                    onClick={resetDesafioForm}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={savingDesafio}
                                >
                                    {savingDesafio ? "Guardando..." : "Guardar desafío"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* MODAL EDITAR DESAFÍO */}
            <div
                className="modal fade"
                id="editDesafioModal"
                tabIndex="-1"
                aria-labelledby="editDesafioModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <form onSubmit={handleUpdateDesafio}>
                            <div className="modal-header">
                                <div>
                                    <h5 className="modal-title" id="editDesafioModalLabel">
                                        Editar información del desafío
                                    </h5>
                                    <small className="text-muted">
                                        Actualiza la información general, imágenes y descripción.
                                    </small>
                                </div>

                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    disabled={savingDesafioEdit}
                                ></button>
                            </div>

                            <div
                                className="modal-body"
                                style={{
                                    maxHeight: "calc(100vh - 180px)",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                }}
                            >
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            Título del desafío <span className="text-danger">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editDesafioForm.titulo}
                                            onChange={(e) =>
                                                handleEditDesafioChange("titulo", e.target.value)
                                            }
                                            maxLength={180}
                                            disabled={savingDesafioEdit}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Título del producto</label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            value={editDesafioForm.productoTitulo}
                                            onChange={(e) =>
                                                handleEditDesafioChange("productoTitulo", e.target.value)
                                            }
                                            maxLength={180}
                                            disabled={savingDesafioEdit}
                                        />
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Imagen principal</label>

                                        {editDesafioForm.currentImagenMiniatura && (
                                            <div className="mb-2">
                                                <img
                                                    src={editDesafioForm.currentImagenMiniatura}
                                                    alt="Imagen principal actual"
                                                    style={{
                                                        width: "100%",
                                                        maxHeight: "180px",
                                                        objectFit: "cover",
                                                        borderRadius: "12px",
                                                    }}
                                                />
                                            </div>
                                        )}

                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/png,image/jpeg,image/webp"
                                            onChange={(e) =>
                                                handleEditDesafioFileChange(
                                                    "imagenMiniaturaFile",
                                                    e.target.files?.[0]
                                                )
                                            }
                                            disabled={savingDesafioEdit}
                                        />

                                        <small className="text-muted">
                                            Selecciona una nueva imagen solo si deseas reemplazarla.
                                        </small>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Imagen secundaria</label>

                                        {editDesafioForm.currentImagenSecundaria && (
                                            <div className="mb-2">
                                                <img
                                                    src={editDesafioForm.currentImagenSecundaria}
                                                    alt="Imagen secundaria actual"
                                                    style={{
                                                        width: "100%",
                                                        maxHeight: "180px",
                                                        objectFit: "cover",
                                                        borderRadius: "12px",
                                                    }}
                                                />
                                            </div>
                                        )}

                                        <input
                                            type="file"
                                            className="form-control"
                                            accept="image/png,image/jpeg,image/webp"
                                            onChange={(e) =>
                                                handleEditDesafioFileChange(
                                                    "imagenSecundariaFile",
                                                    e.target.files?.[0]
                                                )
                                            }
                                            disabled={savingDesafioEdit}
                                        />

                                        <small className="text-muted">
                                            Selecciona una nueva imagen solo si deseas reemplazarla.
                                        </small>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">¿Tiene vigencia?</label>

                                        <select
                                            className="form-select"
                                            value={editDesafioForm.tieneVigencia}
                                            onChange={(e) =>
                                                handleEditDesafioChange("tieneVigencia", e.target.value)
                                            }
                                            disabled={savingDesafioEdit}
                                        >
                                            <option value="1">Sí</option>
                                            <option value="0">No</option>
                                        </select>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Estado</label>

                                        <select
                                            className="form-select"
                                            value={editDesafioForm.activo}
                                            onChange={(e) =>
                                                handleEditDesafioChange("activo", e.target.value)
                                            }
                                            disabled={savingDesafioEdit}
                                        >
                                            <option value="1">Activo</option>
                                            <option value="0">Inactivo</option>
                                        </select>
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label">Descripción</label>

                                        <ReactQuill
                                            theme="snow"
                                            value={editDesafioForm.descripcion}
                                            onChange={(value) =>
                                                handleEditDesafioChange("descripcion", value)
                                            }
                                            readOnly={savingDesafioEdit}
                                        />
                                    </div>

                                    {renderRecursosFields({
                                        enabled: editRecursosEnabled,
                                        setEnabled: setEditRecursosEnabled,
                                        recursos: editRecursosForm,
                                        setRecursos: setEditRecursosForm,
                                        disabled: savingDesafioEdit,
                                    })}

                                    {renderSesionesFields({
                                        enabled: editSesionesEnabled,
                                        setEnabled: setEditSesionesEnabled,
                                        videos: editVideosForm,
                                        setVideos: setEditVideosForm,
                                        disabled: savingDesafioEdit,
                                    })}
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={savingDesafioEdit}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={savingDesafioEdit}
                                >
                                    {savingDesafioEdit ? "Actualizando..." : "Actualizar desafío"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            {/* MODAL NIVELES */}
            <div
                className="modal fade"
                id="nivelesDesafioModal"
                tabIndex="-1"
                aria-labelledby="nivelesDesafioModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <form onSubmit={handleSaveNiveles}>
                            <div className="modal-header">
                                <div>
                                    <h5
                                        className="modal-title"
                                        id="nivelesDesafioModalLabel"
                                    >
                                        Niveles del desafío
                                    </h5>

                                    {selectedDesafio && (
                                        <small className="text-muted">
                                            {selectedDesafio.titulo}
                                        </small>
                                    )}
                                </div>

                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    disabled={savingNiveles}
                                ></button>
                            </div>

                            <div className="modal-body" style={{
                                maxHeight: "70vh",
                                overflowY: "auto",
                                overflowX: "hidden",
                            }}>
                                {loadingNiveles ? (
                                    <div className="text-center py-4">
                                        <div className="iq-loader-box">
                                            <div className="iq-loader-1"></div>
                                        </div>
                                        <p className="text-muted mt-2">
                                            Cargando niveles...
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="mb-4">
                                            <h6>Niveles actuales</h6>

                                            {currentNiveles.length === 0 ? (
                                                <div className="alert alert-info mb-0">
                                                    Este desafío todavía no tiene niveles.
                                                </div>
                                            ) : (
                                                <div className="table-responsive">
                                                    <table className="table table-sm table-striped">
                                                        <thead>
                                                            <tr>
                                                                <th>Orden</th>
                                                                <th>Nombre</th>
                                                                <th>Meta</th>
                                                                <th>Abono</th>
                                                                <th>Premio imagen</th>
                                                                <th>Estado</th>
                                                                <th>Acción</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {currentNiveles.map((nivel) => (
                                                                <tr key={nivel.nivelId}>
                                                                    <td>{nivel.orden}</td>

                                                                    <td>{nivel.nombre}</td>

                                                                    <td>
                                                                        {nivel.metaImporte
                                                                            ? `$ ${formatNumber(nivel.metaImporte)} USD`
                                                                            : "-"}
                                                                    </td>

                                                                    <td>
                                                                        {nivel.abonoValor !== null &&
                                                                        nivel.abonoValor !== undefined &&
                                                                        nivel.abonoValor !== ""
                                                                            ? `$ ${formatNumber(nivel.abonoValor)} MXN`
                                                                            : "-"} 
                                                                    </td>

                                                                    <td>
                                                                        {nivel.abonoUrl ? (
                                                                            <img
                                                                                src={nivel.abonoUrl}
                                                                                alt="Premio"
                                                                                style={{
                                                                                    width: "48px",
                                                                                    height: "48px",
                                                                                    objectFit: "cover",
                                                                                    borderRadius: "8px",
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            "-"
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        {Number(nivel.activo) === 1 ? (
                                                                            <span className="badge bg-success-subtle p-2 text-success">
                                                                                Activo
                                                                            </span>
                                                                        ) : (
                                                                            <span className="badge bg-danger-subtle p-2 text-danger">
                                                                                Inactivo
                                                                            </span>
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-icon btn-warning-subtle ms-2 btn-sm"
                                                                            onClick={() => handleEditNivel(nivel)}
                                                                            disabled={savingNivelEdit || savingNiveles}
                                                                        >
                                                                            <span class="btn-inner"><svg class="icon-32" width="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="0.4" d="M19.9927 18.9534H14.2984C13.7429 18.9534 13.291 19.4124 13.291 19.9767C13.291 20.5422 13.7429 21.0001 14.2984 21.0001H19.9927C20.5483 21.0001 21.0001 20.5422 21.0001 19.9767C21.0001 19.4124 20.5483 18.9534 19.9927 18.9534Z" fill="currentColor"></path><path d="M10.309 6.90385L15.7049 11.2639C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8344 7.72908 20.8452L4.23696 20.8882C4.05071 20.8903 3.88775 20.7613 3.84542 20.5764L3.05175 17.1258C2.91419 16.4915 3.05175 15.8358 3.45388 15.3306L9.88256 6.95545C9.98627 6.82108 10.1778 6.79743 10.309 6.90385Z" fill="currentColor"></path><path opacity="0.4" d="M18.1208 8.66544L17.0806 9.96401C16.9758 10.0962 16.7874 10.1177 16.6573 10.0124C15.3927 8.98901 12.1545 6.36285 11.2561 5.63509C11.1249 5.52759 11.1069 5.33625 11.2127 5.20295L12.2159 3.95706C13.126 2.78534 14.7133 2.67784 15.9938 3.69906L17.4647 4.87078C18.0679 5.34377 18.47 5.96726 18.6076 6.62299C18.7663 7.3443 18.597 8.0527 18.1208 8.66544Z" fill="currentColor"></path></svg></span>
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>

                                        {editingNivel && (
                                            <div className="card border border-warning mb-4">
                                                <div className="card-header d-flex justify-content-between align-items-center">
                                                    <div>
                                                        <strong>Editar nivel existente</strong>
                                                        <div className="text-muted small">
                                                            Nivel ID: {editingNivel.nivelId}
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={handleCancelEditNivel}
                                                        disabled={savingNivelEdit}
                                                    >
                                                        Cancelar edición
                                                    </button>
                                                </div>

                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-8">
                                                            <label className="form-label">Nombre del nivel *</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={editingNivel.nombre}
                                                                onChange={(e) =>
                                                                    updateEditingNivelField("nombre", e.target.value)
                                                                }
                                                                maxLength={120}
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-4">
                                                            <label className="form-label">Orden *</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={editingNivel.orden}
                                                                onChange={(e) =>
                                                                    updateEditingNivelField("orden", e.target.value)
                                                                }
                                                                min="1"
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-12">
                                                            <label className="form-label">Descripción del nivel</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="2"
                                                                value={editingNivel.descripcion}
                                                                onChange={(e) =>
                                                                    updateEditingNivelField("descripcion", e.target.value)
                                                                }
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-4" style={{ display: "none" }}>
                                                            <label className="form-label">Tipo de meta</label>
                                                            <select
                                                                className="form-select"
                                                                value="IMPORTE"
                                                                onChange={() => {}}
                                                                disabled={savingNivelEdit}
                                                            >
                                                                <option value="IMPORTE">Importe</option>
                                                            </select>
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label">Meta importe USD *</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={editingNivel.metaImporte}
                                                                onChange={(e) =>
                                                                    updateEditingNivelField("metaImporte", e.target.value)
                                                                }
                                                                min="0"
                                                                step="0.01"
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-4" style={{ display: "none" }}>
                                                            <label className="form-label">ID moneda meta</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={META_MONEDA_USD_ID}
                                                                readOnly
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label">Valor del abono MXN</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={editingNivel.abonoValor}
                                                                onChange={(e) =>
                                                                    updateEditingNivelField("abonoValor", e.target.value)
                                                                }
                                                                min="0"
                                                                step="0.01"
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label">Nombre del premio</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={editingNivel.premioNombre}
                                                                onChange={(e) =>
                                                                    updateEditingNivelField("premioNombre", e.target.value)
                                                                }
                                                                maxLength={180}
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-12">
                                                            <label className="form-label">
                                                                Imagen del premio
                                                            </label>

                                                            {editingNivel.abonoUrl && (
                                                                <div className="mb-2">
                                                                    <img
                                                                        src={editingNivel.abonoUrl}
                                                                        alt="Premio actual"
                                                                        style={{
                                                                            width: "100%",
                                                                            maxHeight: "160px",
                                                                            objectFit: "cover",
                                                                            borderRadius: "12px",
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}

                                                            <input
                                                                type="file"
                                                                className="form-control"
                                                                accept="image/png,image/jpeg,image/webp"
                                                                onChange={(e) =>
                                                                    updateEditingNivelField(
                                                                        "abonoImagenFile",
                                                                        e.target.files?.[0] || null
                                                                    )
                                                                }
                                                                disabled={savingNivelEdit}
                                                            />

                                                            <small className="text-muted">
                                                                Selecciona una imagen solo si deseas reemplazar la actual.
                                                            </small>
                                                        </div>

                                                        <div className="col-md-12">
                                                            <label className="form-label">Descripción del premio</label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="2"
                                                                value={editingNivel.premioDescripcion}
                                                                onChange={(e) =>
                                                                    updateEditingNivelField(
                                                                        "premioDescripcion",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-6" style={{ display: "none" }}>
                                                            <label className="form-label">Tipo de abono</label>
                                                            <select
                                                                className="form-select"
                                                                value="MONTO"
                                                                onChange={() => {}}
                                                                disabled={savingNivelEdit}
                                                            >
                                                                <option value="MONTO">Monto</option>
                                                            </select>
                                                        </div>

                                                        <div className="col-md-6" style={{ display: "none" }}>
                                                            <label className="form-label">ID moneda abono</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={ABONO_MONEDA_MXN_ID}
                                                                readOnly
                                                                disabled={savingNivelEdit}
                                                            />
                                                        </div>

                                                        <div className="col-md-4" style={{ display: "none" }}>
                                                            <label className="form-label">Estado</label>
                                                            <select
                                                                className="form-select"
                                                                value="1"
                                                                onChange={() => {}}
                                                                disabled={savingNivelEdit}
                                                            >
                                                                <option value="1">Activo</option>
                                                            </select>
                                                        </div>

                                                        <div className="col-md-12 d-flex justify-content-end gap-2">
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary"
                                                                onClick={handleCancelEditNivel}
                                                                disabled={savingNivelEdit}
                                                            >
                                                                Cancelar
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-warning"
                                                                disabled={savingNivelEdit}
                                                                onClick={handleUpdateNivel}
                                                            >
                                                                {savingNivelEdit ? "Actualizando..." : "Actualizar nivel"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <hr />

                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div>
                                                <h6 className="mb-0">Agregar nuevos niveles</h6>
                                                <small className="text-muted">
                                                    Puedes agregar uno o más niveles al desafío.
                                                </small>
                                            </div>

                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={addNivelRow}
                                                disabled={savingNiveles}
                                            >
                                                + Agregar nivel
                                            </button>
                                        </div>

                                        {nivelesForm.map((nivel, index) => (
                                            <div className="card border mb-3" key={index}>
                                                <div className="card-header d-flex justify-content-between align-items-center">
                                                    <strong>Nuevo nivel {index + 1}</strong>

                                                    {nivelesForm.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-danger btn-sm"
                                                            onClick={() => removeNivelRow(index)}
                                                            disabled={savingNiveles}
                                                        >
                                                            Eliminar
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="card-body">
                                                    <div className="row g-3">
                                                        <div className="col-md-8">
                                                            <label className="form-label">
                                                                Nombre del nivel *
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={nivel.nombre}
                                                                onChange={(e) =>
                                                                    updateNivelField(
                                                                        index,
                                                                        "nombre",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                maxLength={120}
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-4">
                                                            <label className="form-label">
                                                                Orden *
                                                            </label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={nivel.orden}
                                                                onChange={(e) =>
                                                                    updateNivelField(
                                                                        index,
                                                                        "orden",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                min="1"
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-12">
                                                            <label className="form-label">
                                                                Descripción del nivel
                                                            </label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="2"
                                                                value={nivel.descripcion}
                                                                onChange={(e) =>
                                                                    updateNivelField(
                                                                        index,
                                                                        "descripcion",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-12">
                                                            <label className="form-label">
                                                                Nombre del premio
                                                            </label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={nivel.premioNombre}
                                                                onChange={(e) =>
                                                                    updateNivelField(
                                                                        index,
                                                                        "premioNombre",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                maxLength={180}
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-4" style={{ display: "none" }}>
                                                            <label className="form-label">
                                                                Tipo de meta *
                                                            </label>

                                                            <select
                                                                className="form-select"
                                                                value={DEFAULT_META_TIPO}
                                                                onChange={() => {}}
                                                                disabled={savingNiveles}
                                                            >
                                                                <option value="IMPORTE">Importe</option>
                                                            </select>
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label">
                                                                Meta importe USD *
                                                            </label>

                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={nivel.metaImporte}
                                                                onChange={(e) =>
                                                                    updateNivelField(index, "metaImporte", e.target.value)
                                                                }
                                                                min="0"
                                                                step="0.01"
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-4" style={{ display: "none" }}>
                                                            <label className="form-label">
                                                                Meta piezas
                                                            </label>

                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={nivel.metaPiezas}
                                                                onChange={(e) =>
                                                                    updateNivelField(index, "metaPiezas", e.target.value)
                                                                }
                                                                min="0"
                                                                step="0.01"
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-4" style={{ display: "none" }}>
                                                            <label className="form-label">
                                                                ID moneda meta
                                                            </label>

                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={META_MONEDA_USD_ID}
                                                                readOnly
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-6" style={{ display: "none" }}>
                                                            <label className="form-label">
                                                                Tipo de abono
                                                            </label>

                                                            <select
                                                                className="form-select"
                                                                value={DEFAULT_ABONO_TIPO}
                                                                onChange={() => {}}
                                                                disabled={savingNiveles}
                                                            >
                                                                <option value="MONTO">Monto</option>
                                                            </select>
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label">
                                                                Valor del abono MXN
                                                            </label>

                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={nivel.abonoValor}
                                                                onChange={(e) =>
                                                                    updateNivelField(index, "abonoValor", e.target.value)
                                                                }
                                                                min="0"
                                                                step="0.01"
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-6">
                                                            <label className="form-label">
                                                                Imagen del premio
                                                            </label>

                                                            <input
                                                                type="file"
                                                                className="form-control"
                                                                accept="image/png,image/jpeg,image/webp"
                                                                onChange={(e) =>
                                                                    updateNivelField(index, "abonoImagenFile", e.target.files?.[0] || null)
                                                                }
                                                                disabled={savingNiveles}
                                                            />

                                                            {nivel.abonoImagenFile && (
                                                                <small className="text-success">
                                                                    Archivo seleccionado: {nivel.abonoImagenFile.name}
                                                                </small>
                                                            )}
                                                        </div>

                                                        <div className="col-md-6" style={{ display: "none" }}>
                                                            <label className="form-label">
                                                                ID moneda abono
                                                            </label>

                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={ABONO_MONEDA_MXN_ID}
                                                                readOnly
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-12" style={{ display: "none" }}>
                                                            <label className="form-label">
                                                                Descripción del premio
                                                            </label>
                                                            <textarea
                                                                className="form-control"
                                                                rows="2"
                                                                value={nivel.premioDescripcion}
                                                                onChange={(e) =>
                                                                    updateNivelField(
                                                                        index,
                                                                        "premioDescripcion",
                                                                        e.target.value
                                                                    )
                                                                }
                                                                disabled={savingNiveles}
                                                            />
                                                        </div>

                                                        <div className="col-md-4" style={{ display: "none" }}>
                                                            <label className="form-label">
                                                                Estado
                                                            </label>

                                                            <select
                                                                className="form-select"
                                                                value={DEFAULT_NIVEL_ACTIVO}
                                                                onChange={() => {}}
                                                                disabled={savingNiveles}
                                                            >
                                                                <option value="1">Activo</option>
                                                            </select>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={savingNiveles}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={savingNiveles || loadingNiveles}
                                >
                                    {savingNiveles ? "Guardando..." : "Guardar niveles"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChampionsList;